import { Box, Button, Container, Skeleton, Tab, Tabs, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useMemo, useState } from "react";
import { sortBy } from "lodash";
import { Navigate, useNavigate } from "react-router-dom";

import { useOrgConfig } from "~/providers/OrgConfigProvider";
import ClinicalConstraintTemplate from "./ClinicalConstraintTemplate";
import {
  useGetClinicalConstraintTemplatesForConfigQuery,
} from "~/graphql/__generated__/graphql";
import CreateClinicalConstraintTemplateDialog from "./CreateClinicalConstraintTemplateDialog";

type TabClinicalTemplatesProps = {
  id?: number;
};

export default function TabClinicalTemplates({ id }: TabClinicalTemplatesProps) {
  const orgConfig = useOrgConfig();
  const navigate = useNavigate();

  const { data, loading, error, refetch } = useGetClinicalConstraintTemplatesForConfigQuery({
    fetchPolicy: "cache-and-network",
  });

  const templates = data?.clinicalConstraintTemplate ?? [];
  const sortedTemplates = useMemo(() => sortBy(templates, (t) => t.name?.toLowerCase?.() ?? ""), [templates]);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const createDialog = (
    <CreateClinicalConstraintTemplateDialog
      open={createDialogOpen}
      onClose={() => setCreateDialogOpen(false)}
    />
  );

  if (error) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Clinical Templates
        </Typography>
        <Typography variant="body2" sx={{ color: "error.main", mb: 2 }}>
          Couldn’t load templates. Please try again.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => {
            void refetch();
          }}
          sx={{ mr: 2 }}
        >
          Retry
        </Button>
        <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>
          Create Template
        </Button>

        {createDialog}
      </Container>
    );
  }

  const initialLoading = loading && !data;

  if (initialLoading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={2}>
          {/* Left side: skeleton tab list */}
          <Grid size={{ xs: 12, md: 4, lg: 3, xl: 2 }}>
            {[0, 1, 2, 3].map((i) => (
              <Box key={i} sx={{ py: 1.2, px: 1, borderBottom: "1px solid", borderColor: "divider" }}>
                <Skeleton variant="text" width="70%" height={20} />
                <Skeleton variant="text" width="50%" height={14} sx={{ mt: 0.3 }} />
              </Box>
            ))}
          </Grid>

          {/* Right side: skeleton form card */}
          <Grid size={{ xs: 12, md: 8, lg: 9, xl: 10 }}>
            <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
              {/* Title */}
              <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
              {/* Service line text */}
              <Skeleton variant="text" width={180} height={18} sx={{ mb: 2 }} />
              {/* Template Name field */}
              <Skeleton variant="rounded" width="100%" height={40} sx={{ mb: 2 }} />
              {/* Description field */}
              <Skeleton variant="rounded" width="100%" height={40} sx={{ mb: 2 }} />
              {/* Care requirement rows */}
              {[0, 1].map((i) => (
                <Skeleton key={i} variant="rounded" width="100%" height={44} sx={{ mb: 1 }} />
              ))}
              {/* + Add New Row button */}
              <Skeleton variant="rounded" width="100%" height={36} sx={{ mt: 1, mb: 3 }} />
              {/* Action buttons */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Skeleton variant="rounded" width={64} height={32} />
                <Skeleton variant="rounded" width={64} height={32} />
                <Box sx={{ flexGrow: 1 }} />
                <Skeleton variant="rounded" width={120} height={32} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    );
  }

  if (sortedTemplates.length === 0) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Clinical Templates
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          No templates yet.
        </Typography>
        <Button variant="contained" onClick={() => setCreateDialogOpen(true)}>
          Create Template
        </Button>

        {createDialog}
      </Container>
    );
  }

  const tab = id;
  if (!tab) {
    const firstId = sortedTemplates[0]?.id;
    if (firstId) {
      return <Navigate to={`/operate-config/clinical-templates/${firstId}`} replace />;
    }
  }

  const selectedTemplate = tab
    ? sortedTemplates.find((t) => t.id === tab)
    : undefined;

  if (tab && !selectedTemplate) {
    const firstId = sortedTemplates[0]?.id;
    if (firstId) {
      return <Navigate to={`/operate-config/clinical-templates/${firstId}`} replace />;
    }
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4, lg: 3, xl: 2 }}>
          <Tabs
            value={tab ?? false}
            onChange={(_, newValue) => {
              navigate(`/operate-config/clinical-templates/${newValue as number}`);
            }}
            orientation="vertical"
            variant="scrollable"
            sx={{ maxHeight: 400 }}
            visibleScrollbar={true}
          >
            {sortedTemplates.map((t) => (
              <Tab
                key={t.id}
                value={t.id}
                sx={{ alignItems: "flex-start" }}
                label={
                  <Box sx={{ textAlign: "left", width: "100%" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                      {t.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.2 }}>
                      {orgConfig.serviceLines[t.serviceLineId]?.name ?? `Service Line ${t.serviceLineId}`}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Tabs>

          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Template
          </Button>

          {createDialog}
        </Grid>

        <Grid size={{ xs: 12, md: 8, lg: 9, xl: 10 }}>
          {selectedTemplate ? (
            <ClinicalConstraintTemplate
              key={selectedTemplate.id}
              template={selectedTemplate}
              onDeleted={() => {
                navigate("/operate-config/clinical-templates");
              }}
            />
          ) : null}
        </Grid>
      </Grid>
    </Container>
  );
}
